export const MAX_SKILL_USER_FILE_IDS = 200;
export const MAX_EFFECTIVE_USER_FILE_IDS = 1000;

export interface PromptFileSelection {
  manualFileIds: string[];
  skillFileIdsBySkillId: Record<string, string[]>;
  suppressedSkillFileIds: string[];
  appliedSkillKeys: string[];
}

export type PromptFileSelectionErrorCode =
  | "invalid_skill_id"
  | "skill_file_limit"
  | "effective_file_limit";

export interface PromptFileSelectionError {
  code: PromptFileSelectionErrorCode;
  message: string;
  limit?: number;
  actual?: number;
  skillId?: string;
}

/**
 * The absent sibling is declared on each member because this project compiles
 * without `strictNullChecks`, where a falsy check on `ok` cannot exclude the
 * success member. Declaring both keys keeps `result.error` readable after
 * `if (!result.ok)` without a cast.
 */
export type PromptFileSelectionResult =
  | { ok: true; selection: PromptFileSelection; error?: undefined }
  | { ok: false; selection?: undefined; error: PromptFileSelectionError };

export type PromptFileSelectionAction =
  | { type: "replace-manual-files"; fileIds: readonly string[] }
  | { type: "replace-visible-files"; fileIds: readonly string[] }
  | {
      type: "apply-skill-files";
      promptGeneration: string | number;
      skillId: string;
      fileIds: readonly string[];
    }
  | { type: "remove-file"; fileId: string }
  | { type: "remove-skill"; promptGeneration: string | number; skillId: string }
  | { type: "retain-skill-files"; fileIds: readonly string[] }
  | { type: "start-prompt-generation" };

function normalizeFileIds(fileIds: readonly string[]): string[] {
  return Array.from(
    new Set(
      fileIds
        .filter((fileId): fileId is string => typeof fileId === "string")
        .map((fileId) => fileId.trim())
        .filter(Boolean)
    )
  );
}

function successful(selection: PromptFileSelection): PromptFileSelectionResult {
  return { ok: true, selection };
}

function effectiveLimitError(actual: number): PromptFileSelectionResult {
  return {
    ok: false,
    error: {
      code: "effective_file_limit",
      message: `Your Files supports at most ${MAX_EFFECTIVE_USER_FILE_IDS} selected files per prompt. Remove files before continuing.`,
      limit: MAX_EFFECTIVE_USER_FILE_IDS,
      actual,
    },
  };
}

function validateEffectiveLimit(selection: PromptFileSelection): PromptFileSelectionResult {
  const effectiveFileIds = getEffectiveUserFileIds(selection);
  return effectiveFileIds.length > MAX_EFFECTIVE_USER_FILE_IDS
    ? effectiveLimitError(effectiveFileIds.length)
    : successful(selection);
}

export function createPromptFileSelection(): PromptFileSelection {
  return {
    manualFileIds: [],
    skillFileIdsBySkillId: {},
    suppressedSkillFileIds: [],
    appliedSkillKeys: [],
  };
}

export function getAppliedSkillKey(promptGeneration: string | number, skillId: string): string {
  return `${promptGeneration}:${skillId.trim()}`;
}

export function getEffectiveUserFileIds(selection: PromptFileSelection): string[] {
  const effectiveFileIds = normalizeFileIds(selection.manualFileIds);
  const seen = new Set(effectiveFileIds);
  const suppressed = new Set(normalizeFileIds(selection.suppressedSkillFileIds));

  for (const fileIds of Object.values(selection.skillFileIdsBySkillId)) {
    for (const fileId of normalizeFileIds(fileIds)) {
      if (suppressed.has(fileId) || seen.has(fileId)) continue;
      seen.add(fileId);
      effectiveFileIds.push(fileId);
    }
  }

  return effectiveFileIds;
}

export function getSkillContributorsForFileId(
  selection: PromptFileSelection,
  fileId: string
): string[] {
  const normalizedFileId = fileId.trim();
  if (!normalizedFileId || selection.suppressedSkillFileIds.includes(normalizedFileId)) {
    return [];
  }

  return Object.entries(selection.skillFileIdsBySkillId)
    .filter(([, fileIds]) => normalizeFileIds(fileIds).includes(normalizedFileId))
    .map(([skillId]) => skillId);
}

export function reducePromptFileSelection(
  selection: PromptFileSelection,
  action: PromptFileSelectionAction
): PromptFileSelectionResult {
  switch (action.type) {
    case "replace-manual-files":
      return validateEffectiveLimit({
        ...selection,
        manualFileIds: normalizeFileIds(action.fileIds),
      });

    case "replace-visible-files": {
      const nextVisibleFileIds = normalizeFileIds(action.fileIds);
      if (nextVisibleFileIds.length > MAX_EFFECTIVE_USER_FILE_IDS) {
        return effectiveLimitError(nextVisibleFileIds.length);
      }

      const currentEffectiveFileIds = getEffectiveUserFileIds(selection);
      const currentEffectiveSet = new Set(currentEffectiveFileIds);
      const nextVisibleSet = new Set(nextVisibleFileIds);
      const manualFileIds = normalizeFileIds(selection.manualFileIds).filter((fileId) =>
        nextVisibleSet.has(fileId)
      );

      for (const fileId of nextVisibleFileIds) {
        if (!currentEffectiveSet.has(fileId)) {
          manualFileIds.push(fileId);
        }
      }

      const suppressedSkillFileIds = normalizeFileIds(selection.suppressedSkillFileIds);
      const suppressedSet = new Set(suppressedSkillFileIds);
      for (const fileId of currentEffectiveFileIds) {
        if (!nextVisibleSet.has(fileId) && !suppressedSet.has(fileId)) {
          suppressedSet.add(fileId);
          suppressedSkillFileIds.push(fileId);
        }
      }

      return validateEffectiveLimit({
        ...selection,
        manualFileIds,
        suppressedSkillFileIds,
      });
    }

    case "apply-skill-files": {
      const skillId = action.skillId.trim();
      if (!skillId) {
        return {
          ok: false,
          error: {
            code: "invalid_skill_id",
            message: "A Skill file contribution requires a valid Skill identifier.",
          },
        };
      }

      const appliedSkillKey = getAppliedSkillKey(action.promptGeneration, skillId);
      if (selection.appliedSkillKeys.includes(appliedSkillKey)) {
        return successful(selection);
      }

      const fileIds = normalizeFileIds(action.fileIds);
      if (fileIds.length > MAX_SKILL_USER_FILE_IDS) {
        return {
          ok: false,
          error: {
            code: "skill_file_limit",
            message: `This Skill references ${fileIds.length} files, but a Skill can attach at most ${MAX_SKILL_USER_FILE_IDS}. Remove files from the Skill and retry.`,
            limit: MAX_SKILL_USER_FILE_IDS,
            actual: fileIds.length,
            skillId,
          },
        };
      }

      return validateEffectiveLimit({
        ...selection,
        skillFileIdsBySkillId: {
          ...selection.skillFileIdsBySkillId,
          [skillId]: fileIds,
        },
        appliedSkillKeys: [...selection.appliedSkillKeys, appliedSkillKey],
      });
    }

    case "remove-file": {
      const fileId = action.fileId.trim();
      if (!fileId) return successful(selection);

      const suppressedSkillFileIds = normalizeFileIds(selection.suppressedSkillFileIds);
      if (!suppressedSkillFileIds.includes(fileId)) {
        suppressedSkillFileIds.push(fileId);
      }

      return successful({
        ...selection,
        manualFileIds: normalizeFileIds(selection.manualFileIds).filter(
          (manualFileId) => manualFileId !== fileId
        ),
        suppressedSkillFileIds,
      });
    }

    case "remove-skill": {
      const skillId = action.skillId.trim();
      if (!skillId) return successful(selection);
      const skillFileIdsBySkillId = { ...selection.skillFileIdsBySkillId };
      delete skillFileIdsBySkillId[skillId];
      // The applied key goes with the contribution. Leaving it behind makes
      // hydration a once-per-prompt event, so re-adding the same Skill before
      // submitting shows its chip with none of its files attached.
      const appliedSkillKey = getAppliedSkillKey(action.promptGeneration, skillId);
      return successful({
        ...selection,
        skillFileIdsBySkillId,
        appliedSkillKeys: selection.appliedSkillKeys.filter((key) => key !== appliedSkillKey),
      });
    }

    case "retain-skill-files": {
      // A Skill's files are prompt-scoped until the request that used them is
      // accepted; from then on they are the conversation's own attachments, the
      // same as if the user had picked them by hand. A file the user unchecked
      // is not retained — the tombstone outranks the Skill that offered it.
      const suppressed = new Set(normalizeFileIds(selection.suppressedSkillFileIds));
      const manualFileIds = normalizeFileIds(selection.manualFileIds);
      const seen = new Set(manualFileIds);
      for (const fileId of normalizeFileIds(action.fileIds)) {
        if (suppressed.has(fileId) || seen.has(fileId)) continue;
        seen.add(fileId);
        manualFileIds.push(fileId);
      }
      return validateEffectiveLimit({ ...selection, manualFileIds });
    }

    case "start-prompt-generation":
      return successful({
        manualFileIds: normalizeFileIds(selection.manualFileIds),
        skillFileIdsBySkillId: {},
        suppressedSkillFileIds: [],
        appliedSkillKeys: [],
      });
  }
}

export function replaceVisibleUserFileIds(
  selection: PromptFileSelection,
  fileIds: readonly string[]
): PromptFileSelectionResult {
  return reducePromptFileSelection(selection, {
    type: "replace-visible-files",
    fileIds,
  });
}

export function applySkillUserFileIds(
  selection: PromptFileSelection,
  promptGeneration: string | number,
  skillId: string,
  fileIds: readonly string[]
): PromptFileSelectionResult {
  return reducePromptFileSelection(selection, {
    type: "apply-skill-files",
    promptGeneration,
    skillId,
    fileIds,
  });
}

export function removePromptFile(
  selection: PromptFileSelection,
  fileId: string
): PromptFileSelection {
  const result = reducePromptFileSelection(selection, { type: "remove-file", fileId });
  return result.ok ? result.selection : selection;
}

export function removeSkillFileContribution(
  selection: PromptFileSelection,
  promptGeneration: string | number,
  skillId: string
): PromptFileSelection {
  const result = reducePromptFileSelection(selection, {
    type: "remove-skill",
    promptGeneration,
    skillId,
  });
  return result.ok ? result.selection : selection;
}

/**
 * The files the given Skills currently contribute, in selection order and
 * without the ones the user unchecked. Reads only what a Skill attached to the
 * composer: a Skill's target and template files live in the run's research
 * scope, never here, so they cannot be retained by mistake.
 */
export function getSkillContributedFileIds(
  selection: PromptFileSelection,
  skillIds: readonly string[]
): string[] {
  const suppressed = new Set(normalizeFileIds(selection.suppressedSkillFileIds));
  const contributed: string[] = [];
  const seen = new Set<string>();

  for (const skillId of skillIds) {
    const fileIds = selection.skillFileIdsBySkillId[skillId?.trim()];
    if (!fileIds) continue;
    for (const fileId of normalizeFileIds(fileIds)) {
      if (suppressed.has(fileId) || seen.has(fileId)) continue;
      seen.add(fileId);
      contributed.push(fileId);
    }
  }

  return contributed;
}

export function retainSkillUserFileIds(
  selection: PromptFileSelection,
  fileIds: readonly string[]
): PromptFileSelectionResult {
  return reducePromptFileSelection(selection, { type: "retain-skill-files", fileIds });
}

export function startPromptFileGeneration(selection: PromptFileSelection): PromptFileSelection {
  const result = reducePromptFileSelection(selection, { type: "start-prompt-generation" });
  return result.ok ? result.selection : selection;
}
